import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, User, FileText, MapPin, Briefcase, Gift, DollarSign, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Checkbox } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { useEmployeeHCM, useUpdateEmployee, useDepartments, useJobPositions } from '../hooks/useHCM';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@travellegacy/nexops-design-system';
import { Link } from 'react-router-dom';
import { applyCPFMask, applyPhoneMask, applyCEPMask, removeMask } from '@/shared/lib/utils';

export function EditEmployeePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: employee, isLoading: isLoadingEmployee } = useEmployeeHCM(id);
  const updateEmployee = useUpdateEmployee();
  const { data: departments = [] } = useDepartments();
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const { data: jobPositions = [] } = useJobPositions({
    departmentId: selectedDepartment || undefined,
  });

  const [formData, setFormData] = useState({
    // Dados de usuário
    name: '',
    email: '',
    password: '', // Opcional, apenas se quiser alterar

    // Contato Corporativo
    whatsapp: '',
    slackUserId: '',

    // Dados pessoais
    rg: '',
    cpf: '',
    birthDate: '',
    phone: '',
    pixKey: '',
    bankAccount: '',
    notionName: '', // ID da relation do Emissor no Notion

    // Endereço
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',

    // Dados contratuais
    admissionDate: '',
    contractType: 'CLT' as 'CLT' | 'PJ',
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'VACATION',
    jobPositionId: '',
    managerId: '',

    // Benefícios
    benefitVAVR: null as number | null,
    benefitHomeOffice: null as number | null,
    benefitVT: null as number | null,
    benefitVTDiscount: 0,
    benefitVTPaymentMethod: 'PLUXEE' as 'PLUXEE' | 'PIX',
    hasGympass: false,

    // Dados de Sócio
    isPartner: false,
    partnershipPercentage: 0,
    monthlyProfitAdvance: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingCep, setLoadingCep] = useState(false);

  // Preencher formulário quando os dados do colaborador forem carregados
  useEffect(() => {
    if (employee) {
      const birthDateFormatted = employee.birthDate
        ? new Date(employee.birthDate).toISOString().split('T')[0]
        : '';
      const admissionDateFormatted = employee.admissionDate
        ? new Date(employee.admissionDate).toISOString().split('T')[0]
        : '';

      setFormData({
        name: employee.user?.name || '',
        email: employee.user?.email || '',
        password: '', // Não preencher senha por segurança
        whatsapp: employee.user?.whatsapp || '',
        slackUserId: employee.user?.slackUserId || '',
        rg: employee.rg || '',
        cpf: employee.cpf ? applyCPFMask(employee.cpf) : '',
        birthDate: birthDateFormatted,
        phone: employee.phone ? applyPhoneMask(employee.phone) : '',
        pixKey: employee.pixKey || '',
        bankAccount: employee.bankAccount || '',
        notionName: employee.notionName || '',
        zipCode: employee.zipCode ? applyCEPMask(employee.zipCode) : '',
        street: employee.street || '',
        number: employee.number || '',
        complement: employee.complement || '',
        neighborhood: employee.neighborhood || '',
        city: employee.city || '',
        state: employee.state || '',
        admissionDate: admissionDateFormatted,
        contractType: (employee.contractType as 'CLT' | 'PJ') || 'CLT',
        status: (employee.status as 'ACTIVE' | 'INACTIVE' | 'VACATION') || 'ACTIVE',
        jobPositionId: employee.jobPositionId || '',
        managerId: employee.managerId || '',
        benefitVAVR: employee.benefitVAVR || null,
        benefitHomeOffice: employee.benefitHomeOffice || null,
        benefitVT: employee.benefitVT || null,
        benefitVTDiscount: employee.benefitVTDiscount || 0,
        benefitVTPaymentMethod: employee.benefitVTPaymentMethod ?? 'PLUXEE',
        hasGympass: employee.hasGympass || false,
        isPartner: employee.isPartner || false,
        partnershipPercentage: employee.partnershipPercentage || 0,
        monthlyProfitAdvance: employee.monthlyProfitAdvance || 0,
      });

      // Definir departamento selecionado se houver cargo
      if (employee.jobPosition?.departmentId) {
        setSelectedDepartment(employee.jobPosition.departmentId);
      }
    }
  }, [employee]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handlers especiais com máscaras
  const handleCPFChange = (value: string) => {
    const formatted = applyCPFMask(value);
    setFormData((prev) => ({ ...prev, cpf: formatted }));
    if (errors.cpf) {
      setErrors((prev) => ({ ...prev, cpf: '' }));
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = applyPhoneMask(value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  // Buscar endereço pelo CEP
  const fetchAddressByCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      return;
    }

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setErrors((prev) => ({ ...prev, zipCode: 'CEP não encontrado' }));
        return;
      }

      // Preencher os campos automaticamente
      setFormData((prev) => ({
        ...prev,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
        complement: data.complemento || prev.complement,
      }));

      // Limpar erro de CEP se houver
      setErrors((prev) => ({ ...prev, zipCode: '' }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setErrors((prev) => ({ ...prev, zipCode: 'Erro ao buscar CEP' }));
    } finally {
      setLoadingCep(false);
    }
  };

  // Handler especial para CEP
  const handleCepChange = (value: string) => {
    const formatted = applyCEPMask(value);
    setFormData((prev) => ({ ...prev, zipCode: formatted }));

    if (errors.zipCode) {
      setErrors((prev) => ({ ...prev, zipCode: '' }));
    }

    // Buscar endereço quando CEP estiver completo
    const cleanCep = removeMask(formatted);
    if (cleanCep.length === 8) {
      fetchAddressByCep(formatted);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.name && formData.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }

    if (formData.cpf) {
      const cpfNumbers = removeMask(formData.cpf);
      if (cpfNumbers.length !== 11) {
        newErrors.cpf = 'CPF deve ter 11 dígitos';
      }
    }

    if (formData.state && formData.state.length !== 2) {
      newErrors.state = 'Estado deve ter 2 letras (ex: SP)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !id) {
      return;
    }

    try {
      const payload: Record<string, unknown> = {};

      // Dados de usuário
      if (formData.name) payload.name = formData.name;
      if (formData.email) payload.email = formData.email;
      if (formData.password) payload.password = formData.password; // Apenas se preenchido

      // Contato Corporativo
      if (formData.whatsapp) payload.whatsapp = formData.whatsapp;
      if (formData.slackUserId) payload.slackUserId = formData.slackUserId;

      // Adicionar campos apenas se preenchidos ou alterados (removendo máscaras)
      if (formData.rg) payload.rg = formData.rg;
      if (formData.cpf) payload.cpf = removeMask(formData.cpf);
      if (formData.birthDate) payload.birthDate = formData.birthDate;
      if (formData.phone) payload.phone = removeMask(formData.phone);
      if (formData.pixKey) payload.pixKey = formData.pixKey;
      if (formData.bankAccount) payload.bankAccount = formData.bankAccount;
      if (formData.notionName) payload.notionName = formData.notionName;

      if (formData.zipCode) payload.zipCode = removeMask(formData.zipCode);
      if (formData.street) payload.street = formData.street;
      if (formData.number) payload.number = formData.number;
      if (formData.complement) payload.complement = formData.complement;
      if (formData.neighborhood) payload.neighborhood = formData.neighborhood;
      if (formData.city) payload.city = formData.city;
      if (formData.state) payload.state = formData.state;

      if (formData.admissionDate) payload.admissionDate = formData.admissionDate;
      payload.contractType = formData.contractType;
      payload.status = formData.status;
      if (formData.jobPositionId) payload.jobPositionId = formData.jobPositionId;
      if (formData.managerId) payload.managerId = formData.managerId;

      payload.benefitVAVR = formData.benefitVAVR;
      payload.benefitHomeOffice = formData.benefitHomeOffice;
      payload.benefitVT = formData.benefitVT;
      payload.benefitVTDiscount = formData.benefitVTDiscount;
      payload.benefitVTPaymentMethod = formData.benefitVTPaymentMethod;
      payload.hasGympass = formData.hasGympass;

      payload.isPartner = formData.isPartner;
      if (formData.isPartner) {
        payload.partnershipPercentage = formData.partnershipPercentage;
        payload.monthlyProfitAdvance = formData.monthlyProfitAdvance;
      } else {
        payload.partnershipPercentage = null;
        payload.monthlyProfitAdvance = null;
      }

      console.log('Enviando atualização com payload:', payload);
      await updateEmployee.mutateAsync({ id, data: payload });
      navigate(`/people-core/hcm/employees/${id}`);
    } catch (error) {
      console.error('Erro detalhado ao atualizar colaborador:', error);

      // Tentar extrair mensagem de erro mais específica
      let errorMessage = 'Erro ao atualizar colaborador. Verifique os dados e tente novamente.';

      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: {
              error?: {
                message?: string;
              };
            };
          };
          message?: string;
        };

        if (apiError.response?.data?.error?.message) {
          errorMessage = apiError.response.data.error.message;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }

      console.error('Mensagem de erro:', errorMessage);
      setErrors({ submit: errorMessage });
    }
  };

  if (isLoadingEmployee) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!employee) {
    return <div className="p-8 text-center">Colaborador não encontrado</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Editar Colaborador</h1>
          <p className="text-muted-foreground">{employee.user?.name}</p>
        </div>
        <Link to={`/people-core/hcm/employees/${id}`} className={cn(buttonVariants({ variant: 'outline' }))}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Dados de Usuário */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Dados de Usuário
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nome Completo"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={errors.name}
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={errors.email}
                />
                <Input
                  label="Nova Senha (deixe em branco para não alterar)"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  error={errors.password}
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
            </CardContent>
          </Card>

          {/* Dados Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Dados Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label="CPF"
                  value={formData.cpf}
                  onChange={(e) => handleCPFChange(e.target.value)}
                  error={errors.cpf}
                  maxLength={14}
                  placeholder="000.000.000-00"
                />
                <Input
                  label="RG"
                  value={formData.rg}
                  onChange={(e) => handleChange('rg', e.target.value)}
                  placeholder="00.000.000-0"
                />
                <Input
                  label="Data de Nascimento"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                />
                <Input
                  label="Telefone (com DDI)"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  maxLength={19}
                  placeholder="+55 (00) 00000-0000"
                />
                <Input
                  label="Chave Pix"
                  value={formData.pixKey}
                  onChange={(e) => handleChange('pixKey', e.target.value)}
                />
                <Input
                  label="Conta Bancária"
                  value={formData.bankAccount}
                  onChange={(e) => handleChange('bankAccount', e.target.value)}
                  placeholder="Banco, Ag, Conta"
                />
              </div>
              <div className="mt-4 p-4 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Input
                  label="ID Notion (Emissor)"
                  value={formData.notionName}
                  onChange={(e) => handleChange('notionName', e.target.value)}
                  placeholder="ID da relation do Emissor no Notion"
                  helperText="Usado para identificar o colaborador nas emissões do Notion e calcular comissões automaticamente"
                />
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Endereço
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <Input
                  label={loadingCep ? 'CEP (buscando...)' : 'CEP'}
                  value={formData.zipCode}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  error={errors.zipCode}
                  disabled={loadingCep}
                />
                <div className="md:col-span-2">
                  <Input
                    label="Rua"
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                  />
                </div>
                <Input
                  label="Número"
                  value={formData.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                />
                <Input
                  label="Complemento"
                  value={formData.complement}
                  onChange={(e) => handleChange('complement', e.target.value)}
                />
                <Input
                  label="Bairro"
                  value={formData.neighborhood}
                  onChange={(e) => handleChange('neighborhood', e.target.value)}
                />
                <Input
                  label="Cidade"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
                <Input
                  label="Estado (UF)"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                  error={errors.state}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
            </CardContent>
          </Card>

          {/* Dados Contratuais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Dados Contratuais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Data de Admissão"
                  type="date"
                  value={formData.admissionDate}
                  onChange={(e) => handleChange('admissionDate', e.target.value)}
                />
                <Select
                  label="Tipo de Contrato"
                  options={[
                    { label: 'CLT', value: 'CLT' },
                    { label: 'PJ', value: 'PJ' },
                  ]}
                  value={formData.contractType}
                  onChange={(e) => handleChange('contractType', e.target.value)}
                />
                <Select
                  label="Status"
                  options={[
                    { label: 'Ativo', value: 'ACTIVE' },
                    { label: 'Inativo', value: 'INACTIVE' },
                    { label: 'Férias', value: 'VACATION' },
                  ]}
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                />
                <Select
                  label="Departamento"
                  options={[
                    { label: 'Selecione um departamento', value: '' },
                    ...departments.map((dept) => ({ label: dept.name, value: dept.id })),
                  ]}
                  value={selectedDepartment}
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    handleChange('jobPositionId', '');
                  }}
                />
                <Select
                  label="Cargo"
                  options={[
                    { label: 'Selecione um cargo', value: '' },
                    ...jobPositions.map((pos) => ({ label: pos.name, value: pos.id })),
                  ]}
                  value={formData.jobPositionId}
                  onChange={(e) => handleChange('jobPositionId', e.target.value)}
                  disabled={!selectedDepartment}
                />
              </div>
            </CardContent>
          </Card>

          {/* Benefícios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5" />
                Benefícios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="VA/VR (Valor Mensal)"
                    type="number"
                    placeholder="Ex: 500.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVAVR || ''}
                    onChange={(e) => handleChange('benefitVAVR', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                  <Input
                    label="Auxílio Home Office (Valor Mensal)"
                    type="number"
                    placeholder="Ex: 200.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitHomeOffice || ''}
                    onChange={(e) => handleChange('benefitHomeOffice', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                  <Input
                    label="VT - Valor Fornecido (Mensal)"
                    type="number"
                    placeholder="Ex: 300.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVT || ''}
                    onChange={(e) => handleChange('benefitVT', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
                <div>
                  <Input
                    label="VT - Desconto em Folha"
                    type="number"
                    placeholder="Ex: 150.00"
                    step="0.01"
                    min="0"
                    value={formData.benefitVTDiscount}
                    onChange={(e) => handleChange('benefitVTDiscount', parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Valor que será descontado da participação/salário do colaborador
                  </p>
                </div>
                <div>
                  <Select
                    label="VT - Pago via"
                    options={[
                      { label: 'Pluxee (cartão de benefícios)', value: 'PLUXEE' },
                      { label: 'Pix (junto do pagamento)', value: 'PIX' },
                    ]}
                    value={formData.benefitVTPaymentMethod}
                    onChange={(e) => handleChange('benefitVTPaymentMethod', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    No Pix, o VT entra automaticamente em Adicionais na página de Pagamentos e sai da aba Benefícios
                  </p>
                </div>
                <Checkbox
                  id="hasGympass"
                  label="Gympass"
                  checked={formData.hasGympass}
                  onChange={(checked) => handleChange('hasGympass', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Dados de Sócio */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Dados de Sócio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Checkbox
                  id="isPartner"
                  label="É Sócio"
                  checked={formData.isPartner}
                  onChange={(checked) => handleChange('isPartner', checked)}
                />
                {formData.isPartner && (
                  <>
                    <Input
                      id="partnershipPercentage"
                      label="% de Participação"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.partnershipPercentage}
                      onChange={(e) => handleChange('partnershipPercentage', parseFloat(e.target.value) || 0)}
                    />
                    <Input
                      id="monthlyProfitAdvance"
                      label="Antecipação Mensal (R$)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.monthlyProfitAdvance}
                      onChange={(e) => handleChange('monthlyProfitAdvance', parseFloat(e.target.value) || 0)}
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contato Corporativo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Contato Corporativo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="WhatsApp (com código do país)"
                  value={formData.whatsapp}
                  onChange={(e) => handleChange('whatsapp', e.target.value)}
                  placeholder="+55 11 99999-9999"
                  helperText="Usado para envio de lembretes e notificações"
                />
                <Input
                  label="Slack User ID"
                  value={formData.slackUserId}
                  onChange={(e) => handleChange('slackUserId', e.target.value)}
                  placeholder="U01ABC123XYZ"
                  helperText="ID do usuário no workspace do Slack"
                />
              </div>
            </CardContent>
          </Card>

          {errors.submit && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Link to={`/people-core/hcm/employees/${id}`} className={cn(buttonVariants({ variant: 'outline' }))}>
              Cancelar
            </Link>
            <Button type="submit" disabled={updateEmployee.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateEmployee.isPending ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
